import { isCompletePickleballGame } from "@/lib/sports/pickleball";
import { PickleballGameScore } from "@/types";
import { Input } from "@heroui/react";
import { useTranslations } from "next-intl";
import React, { useCallback } from "react";

export interface PickleballMatchResultFormProps {
  value?: { games: PickleballGameScore[] };
  onChange?: (result: { games: PickleballGameScore[] }) => void;
  disabled?: boolean;
  team1Name?: string;
  team2Name?: string;
  team1Id?: string;
  team2Id?: string;
}

export const defaultPickleballGames: PickleballGameScore[] = [
  { team1Points: 0, team2Points: 0 },
  { team1Points: 0, team2Points: 0 },
  { team1Points: 0, team2Points: 0 },
];

const emptyGame = (): PickleballGameScore => ({ team1Points: 0, team2Points: 0 });

const winnerOf = (g: PickleballGameScore): "team1" | "team2" | null => {
  if (!isCompletePickleballGame(g)) return null;
  return g.team1Points > g.team2Points ? "team1" : "team2";
};

/**
 * Best of 5 form. Games 1–3 always shown.
 * Game 4 shown if the game-win tally after 3 is 2-1 (no winner yet).
 * Game 5 shown if the game-win tally after 4 is 2-2.
 * Hidden games are zeroed so we don't persist stray scores.
 */
const PickleballMatchResultForm: React.FC<PickleballMatchResultFormProps> = ({
  value = { games: defaultPickleballGames },
  onChange,
  disabled = false,
  team1Name,
  team2Name,
}) => {
  const t = useTranslations();

  const games = value.games ?? defaultPickleballGames;

  // Pad to 5 slots so we can address game 4 & 5 even when they're hidden.
  const padded: PickleballGameScore[] = [0, 1, 2, 3, 4].map(
    (i) => games[i] ?? emptyGame(),
  );

  const decided: ("team1" | "team2" | null)[] = padded.map(winnerOf);

  // Games 1-3 are always editable. A later game is unlocked when all PRIOR
  // games are decided and the match wasn't already won there. Slots that
  // already have scores stay editable so existing data is never frozen.
  const isGameDisabled = (i: number): boolean => {
    if (i < 3) return false;
    if (padded[i].team1Points > 0 || padded[i].team2Points > 0) return false;
    let priorT1 = 0;
    let priorT2 = 0;
    let priorDecided = 0;
    for (let j = 0; j < i; j++) {
      if (decided[j] === "team1") priorT1++;
      else if (decided[j] === "team2") priorT2++;
      if (decided[j] !== null) priorDecided++;
    }
    if (priorDecided < i) return true;
    return priorT1 >= 3 || priorT2 >= 3;
  };

  // Auto-fill the opponent score so the resulting game is a valid pickleball
  // finish: win at 11 by 2, or cap at 15 (15-14 ends the game).
  const autoFillOpponent = (val: number, currentOther: number): number => {
    if (val >= 0 && val <= 9) return 11;
    if (val === 10) return 12;
    if (val === 11) {
      if (currentOther >= 0 && currentOther <= 9) return currentOther;
      return 9;
    }
    if (val === 12) {
      if (currentOther === 10) return 10;
      if (currentOther >= 13 && currentOther <= 15) return currentOther;
      return 10;
    }
    if (val === 13) {
      if (currentOther === 11 || currentOther === 15) return currentOther;
      return 11;
    }
    if (val === 14) {
      if (currentOther === 12 || currentOther === 15) return currentOther;
      return 12;
    }
    if (val === 15) {
      if (currentOther === 13 || currentOther === 14) return currentOther;
      return 13;
    }
    return currentOther;
  };

  const handleChange = useCallback(
    (index: number, team: "team1Points" | "team2Points", val: number) => {
      if (!onChange) return;
      const safe = Math.max(0, Math.min(15, val));
      const otherTeam = team === "team1Points" ? "team2Points" : "team1Points";

      const next: PickleballGameScore[] = [0, 1, 2, 3, 4].map((i) => {
        const g = games[i] ?? emptyGame();
        if (i === index) {
          const otherFilled = autoFillOpponent(safe, g[otherTeam]);
          return { ...g, [team]: safe, [otherTeam]: otherFilled };
        }
        return g;
      });

      // Zero out games beyond the one that decides the match,
      // so we don't persist stray scores from a longer-form entry.
      let t1 = 0;
      let t2 = 0;
      let decidedAt = -1;
      for (let i = 0; i < next.length; i++) {
        const w = winnerOf(next[i]);
        if (w === "team1") t1++;
        if (w === "team2") t2++;
        if ((t1 >= 3 || t2 >= 3) && decidedAt === -1) decidedAt = i;
      }
      if (decidedAt !== -1) {
        for (let i = decidedAt + 1; i < next.length; i++) {
          next[i] = emptyGame();
        }
      }

      // Trim trailing all-zero games (but keep at least 3 slots).
      let trimmed = [...next];
      while (trimmed.length > 3 && (() => {
        const last = trimmed[trimmed.length - 1];
        return last.team1Points === 0 && last.team2Points === 0;
      })()) {
        trimmed = trimmed.slice(0, -1);
      }

      onChange({ games: trimmed });
    },
    [onChange, games],
  );

  return (
    <div className="flex flex-col gap-4">
      {[0, 1, 2, 3, 4].map((i) => {
        const game = padded[i];
        const team1IsWinner = decided[i] === "team1";
        const team2IsWinner = decided[i] === "team2";
        const lockedOut = isGameDisabled(i);
        const inputsDisabled = disabled || lockedOut;
        const incomplete =
          !lockedOut &&
          (game.team1Points > 0 || game.team2Points > 0) &&
          decided[i] === null;

        return (
          <div
            key={i}
            className={`flex flex-col gap-2 ${lockedOut ? "opacity-50" : ""}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-primary">
                {t("matches.game")} {i + 1}
              </span>
              {incomplete && (
                <span className="text-xs text-warning">
                  {t("matches.incompleteGameScore")}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{team1Name}</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={0}
                  max={15}
                  value={game.team1Points.toString()}
                  isDisabled={inputsDisabled}
                  onChange={(e) =>
                    handleChange(i, "team1Points", Number(e.target.value))
                  }
                  variant="bordered"
                  size="md"
                  classNames={{
                    input: `text-center text-lg font-semibold ${
                      team1IsWinner ? "text-success" : ""
                    }`,
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{team2Name}</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={0}
                  max={15}
                  value={game.team2Points.toString()}
                  isDisabled={inputsDisabled}
                  onChange={(e) =>
                    handleChange(i, "team2Points", Number(e.target.value))
                  }
                  variant="bordered"
                  size="md"
                  classNames={{
                    input: `text-center text-lg font-semibold ${
                      team2IsWinner ? "text-success" : ""
                    }`,
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PickleballMatchResultForm;
